import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  SxProps,
  Tooltip,
  Typography,
} from '@mui/material';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import AudiotrackOutlinedIcon from '@mui/icons-material/AudiotrackOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CollectionsBookmarkOutlinedIcon from '@mui/icons-material/CollectionsBookmarkOutlined';
import VideoChatOutlinedIcon from '@mui/icons-material/VideoChatOutlined';
import LibraryMusicOutlinedIcon from '@mui/icons-material/LibraryMusicOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import InsertLinkOutlinedIcon from '@mui/icons-material/InsertLinkOutlined';
import { getMessageStatistics } from '../api/requests/users';
import ViewRecipientsDialog, { loaderSx } from './ViewRecipientsDialog';
import UserAnalyticsTable from './UserAnalyticsTable';
import { useParams } from 'react-router-dom';
import { analyticsOuterBoxStyles } from './DistributionAnalyticsTable';
import { GridColDef } from '@mui/x-data-grid';
import { formatFullDateTime } from '../utils/utils';
import { sectionDescriptionSx, sectionTitleSx } from './UserAnalytics';
import { PaginationType } from './Groups';
import { textSx } from './MandatoryReplies';
import { colors } from '../utils/constants';

interface MessageStatsType {
  messageType: string;
  messageText: string;
  date: string;
  recipientsCount: number;
  viewedCount: number;
  isGroup: boolean;
  groupName: string | null;
  messageId: number;
  id?: number;
}

interface RowsType extends MessageStatsType {
  id: number;
}

const MessageStatsTable = () => {
  const { userId } = useParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<RowsType[]>([]);
  const [selectedRow, setSelectedRow] = useState<{
    id: number;
  } | null>(null);
  const [paginationModel, setPaginationModel] = useState<PaginationType>({
    page: 0,
    pageSize: 5,
  });
  const [totalRowsCount, setTotalRowsCount] = useState<number>(0);

  const viewDetailsHandler = (id: number) => {
    setSelectedRow({ id });
  };

  const closeDialog = () => {
    setSelectedRow(null);
  };

  const renderProgress = (value: string) => {
    let Icon;

    switch (value) {
      case 'Text':
        Icon = ChatBubbleOutlineOutlinedIcon;
        break;
      case 'Image':
        Icon = ImageOutlinedIcon;
        break;
      case 'ImageWithText':
        Icon = CollectionsBookmarkOutlinedIcon;
        break;
      case 'Video':
        Icon = VideocamOutlinedIcon;
        break;
      case 'VideoWithText':
        Icon = VideoChatOutlinedIcon;
        break;
      case 'Audio':
        Icon = AudiotrackOutlinedIcon;
        break;
      case 'AudioWithText':
        Icon = LibraryMusicOutlinedIcon;
        break;
      case 'Document':
        Icon = DescriptionOutlinedIcon;
        break;
      case 'DocumentWithText':
        Icon = ArticleOutlinedIcon;
        break;
      case 'Media':
        Icon = PermMediaOutlinedIcon;
        break;
      case 'MediaWithText':
        Icon = CollectionsBookmarkOutlinedIcon;
        break;
      case 'Link':
        Icon = LinkOutlinedIcon;
        break;
      case 'LinkWithText':
        Icon = InsertLinkOutlinedIcon;
        break;
      case 'Unknown':
      default:
        Icon = HelpOutlineIcon;
    }
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Icon sx={{ fontSize: 16, color: '#2f2f2f' }} />
        <Typography fontSize='14px'>{value}</Typography>
      </Box>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'messageType',
      headerName: 'Message Type',
      flex: 1,
      renderCell: (params) => renderProgress(params.value),
    },
    {
      field: 'messageText',
      headerName: 'Text',
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography fontSize='14px' sx={textSx}>
            {params.value || '--'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      renderCell: (params) => (
        <Typography fontSize='14px'>{params.value}</Typography>
      ),
    },
    {
      field: 'recipientsCount',
      headerName: 'Recipients',
      flex: 1,
      renderCell: (params) => (
        <Box>
          <Typography fontSize='14px'>{params.value}</Typography>
          {params.row.isGroup && (
            <Typography fontSize='10px' color='gray'>
              Group: {params.row.groupName}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'viewedCount',
      headerName: 'Viewed',
      flex: 1,
      renderCell: (params) => {
        const viewed = params.value;
        const total = params.row.recipientsCount;
        const percentage = total ? ((viewed / total) * 100).toFixed(1) : '0';
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <RemoveRedEyeOutlinedIcon sx={{ fontSize: 16, color: 'gray' }} />
            <Typography fontSize='14px'>{viewed}</Typography>
            <Typography fontSize='14px' sx={{ color: 'gray' }}>
              ({percentage}%)
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography
          sx={viewReciepientStyles}
          onClick={() => viewDetailsHandler(params.row.id)}
        >
          View Recipients
        </Typography>
      ),
    },
  ];

  const fetchMessageStatistics = async () => {
    setLoading(true);
    try {
      const response =
        userId &&
        (await getMessageStatistics({
          userId,
          page: paginationModel.page,
          size: paginationModel.pageSize,
        }));
      if (response && response.userMessageStatistics) {
        const updatedResponse = response.userMessageStatistics.map(
          (item: MessageStatsType) => ({
            ...item,
            id: item.messageId,
            date: formatFullDateTime(item.date),
          })
        );
        setRows(updatedResponse);
        setTotalRowsCount(response.totalCount);
      } else {
        setRows([]);
        setTotalRowsCount(0);
      }
    } catch (error) {
      console.error('Failed to fetch message statistics', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessageStatistics();
  }, [paginationModel]);

  return (
    <Box sx={analyticsOuterBoxStyles}>
      <Typography sx={sectionTitleSx}>
        Message Statistics
        <Typography sx={sectionDescriptionSx}>
          Details of messages sent and their engagement metrics
        </Typography>
      </Typography>
      {loading ? (
        <Box sx={loaderSx}>
          <CircularProgress size={60} thickness={6} />
        </Box>
      ) : rows.length > 0 ? (
        <UserAnalyticsTable
          columns={columns}
          rows={rows}
          sx={{ height: '100%', width: '100%' }}
          setPaginationModel={setPaginationModel}
          paginationModel={paginationModel}
          totalRowsCount={totalRowsCount}
        />
      ) : (
        <Box sx={loaderSx}>
          <Typography variant='body2' color='textSecondary'>
            No data found.
          </Typography>
        </Box>
      )}
      {selectedRow && (
        <ViewRecipientsDialog
          open={!!selectedRow}
          handleClose={closeDialog}
          id={selectedRow.id}
          title='Message Recipients'
          description='List of all recipients and their interaction status'
        />
      )}
    </Box>
  );
};

export default MessageStatsTable;

export const viewReciepientStyles: SxProps = {
  fontWeight: 400,
  fontSize: '14px',
  cursor: 'pointer',
  color: colors.cobaltBlueColor,
};
